/**
 * Sync Manager: Synchronisiert ausstehende Uploads und Einkaufslisten-Aenderungen
 * mit dem Server, sobald eine Netzwerkverbindung verfuegbar ist.
 */

import { getPendingUploads, removePendingUpload, getCachedPendingChanges, clearPendingShoppingChanges } from './offlineDb';
import { receiptsApi } from '../api/receipts';
import { shoppingListsApi } from '../api/shoppingLists';

export interface SyncResult {
  successCount: number;
  failedCount: number;
}

/**
 * Laedt alle ausstehenden Uploads aus der IndexedDB hoch.
 *
 * Gibt die Anzahl erfolgreich hochgeladener und fehlgeschlagener
 * Uploads zurueck.
 */
export async function syncPendingUploads(): Promise<SyncResult> {
  const pending = await getPendingUploads();

  if (pending.length === 0) {
    return { successCount: 0, failedCount: 0 };
  }

  let successCount = 0;
  let failedCount = 0;

  for (const entry of pending) {
    try {
      // Blob zu File konvertieren fuer die Upload-API
      const file = new File([entry.file], entry.fileName, {
        type: entry.file.type,
      });

      await receiptsApi.upload(file);
      await removePendingUpload(entry.id);
      successCount++;
    } catch {
      // Einzelner Fehlschlag blockiert nicht die anderen Uploads
      failedCount++;
    }
  }

  return { successCount, failedCount };
}

/**
 * Synct alle offline gemachten Einkaufslisten-Aenderungen (checked/unchecked)
 * zurueck zum Backend.
 *
 * Gibt die Anzahl erfolgreich gesyncten und fehlgeschlagenen Aenderungen zurueck.
 */
export async function syncShoppingListChanges(): Promise<SyncResult> {
  const pending = await getCachedPendingChanges();

  if (pending.length === 0) {
    return { successCount: 0, failedCount: 0 };
  }

  let successCount = 0;
  let failedCount = 0;

  for (const change of pending) {
    try {
      await shoppingListsApi.updateItem(change.listId, change.id, {
        is_checked: change.is_checked,
      });
      successCount++;
    } catch {
      // Einzelne Fehlschlaege blockieren nicht die anderen Syncs
      failedCount++;
    }
  }

  // Pending Changes loeschen — auch bei teilweisem Erfolg,
  // da bei naechstem Online-Load die Daten frisch vom Server kommen.
  if (successCount > 0 || failedCount === 0) {
    await clearPendingShoppingChanges();
  }

  return { successCount, failedCount };
}
